import React, { useState } from 'react';
import { Button } from 'equasion-lib';
import {
  PICK_SHORT_REASONS,
  PICK_SHORT_REASON_MAX_LENGTH,
  parseShortReason,
  pickUnitScanShortText,
  type PickLine
} from '../../pickLineSteps';

type PickShortReasonFormProps = {
  line: PickLine;
  busy: boolean;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
};

const OTHER = 'Other';
const click = (onClick: () => void) => ({ onClick });

/**
 * The short pick of a unit-scan (controlled) line: no quantity to type — the units scanned are what
 * is picked — only WHY the rest is short, chosen from the usual reasons or written out ("Other").
 * The server requires the reason and flags the shortage for review when Pick completes.
 */
function PickShortReasonForm({ line, busy, onSubmit, onCancel }: PickShortReasonFormProps) {
  const [choice, setChoice] = useState<string | null>(null);
  const [other, setOther] = useState('');
  const reason = parseShortReason(choice === OTHER ? other : choice);

  return (
    <div className="pick-short-reason" role="group" aria-label="Short pick reason">
      <h2>Record a short pick</h2>
      <p>{pickUnitScanShortText(line)}</p>
      <div className="pick-short-reason__choices" role="radiogroup" aria-label="Reason">
        {[...PICK_SHORT_REASONS, OTHER].map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={choice === option}
            className={[
              'pick-short-reason__choice',
              choice === option && 'is-selected'
            ]
              .filter(Boolean)
              .join(' ')}
            disabled={busy}
            onClick={() => setChoice(option)}
          >
            {option}
          </button>
        ))}
      </div>
      {choice === OTHER && (
        <textarea
          aria-label="Reason"
          name="shortReason"
          className="pick-short-reason__other"
          maxLength={PICK_SHORT_REASON_MAX_LENGTH}
          placeholder="Why is it short?"
          value={other}
          disabled={busy}
          onChange={(e) => setOther(e.target.value)}
        />
      )}
      <div className="pick-short-reason__actions">
        <Button
          type="primary"
          fullWidth
          disabled={busy || !reason}
          {...click(() => {
            if (reason) onSubmit(reason);
          })}
        >
          {busy ? 'Saving…' : 'Record short pick'}
        </Button>
        <Button type="secondary" fullWidth disabled={busy} {...click(onCancel)}>
          Back
        </Button>
      </div>
    </div>
  );
}

export default PickShortReasonForm;
